// src/services/array-assertions.ts
import { defined } from "@vy/null";
function isEmpty(array) {
  return !defined(array) || array.length === 0;
}
function isNotEmpty(array) {
  return defined(array) && array.length > 0;
}
function containsManyItems(array) {
  return defined(array) && array.length > 1;
}
function isSingleItem(array) {
  return defined(array) && array.length === 1;
}
function isArray(array) {
  return Array.isArray(array);
}
function isNotIn(array, ...items) {
  return isEmpty(array) || array.every((item) => !items.includes(item));
}

// src/services/array-accessor.ts
import { raiseIfNullish } from "@vy/null";

// src/errors/unexpected-array-size-error.ts
import { VyBaseError } from "@vy/err";

// src/types/array-error-code.ts
var ARRAY_ERROR_CODES = Object.freeze({
  UNEXPECTED_ARRAY_SIZE: "UNEXPECTED_ARRAY_SIZE",
  ARRAY_OUT_OF_BOUNDS: "ARRAY_OUT_OF_BOUNDS",
  ITEM_NOT_FOUND: "ITEM_NOT_FOUND"
});

// src/errors/unexpected-array-size-error.ts
var UnexpectedArraySizeError = class extends VyBaseError {
  constructor(arrayName, array, expectedSize) {
    super({
      code: ARRAY_ERROR_CODES.UNEXPECTED_ARRAY_SIZE,
      message: "Unexpected array size",
      metadata: {
        arrayName,
        array,
        expectedSize
      }
    });
  }
};

// src/errors/array-out-of-bounds-error.ts
import { NullError } from "@vy/null";
var ArrayOutOfBoundsError = class extends NullError {
  constructor(arrayName, array, index) {
    const arrayLength = array.length;
    const baseInput = {
      code: ARRAY_ERROR_CODES.ARRAY_OUT_OF_BOUNDS,
      message: "Array out of bounds",
      metadata: {
        array,
        index,
        arrayLength
      }
    };
    super(arrayName, baseInput);
  }
};

// src/errors/array-is-empty-error.ts
import { VyBaseError as VyBaseError2 } from "@vy/err";
var ArrayIsEmptyError = class extends VyBaseError2 {
  constructor(arrayName, array, message, errorCode) {
    super({
      code: errorCode ?? ARRAY_ERROR_CODES.UNEXPECTED_ARRAY_SIZE,
      message,
      metadata: {
        arrayName,
        array
      }
    });
  }
};

// src/errors/item-not-found-error.ts
import { VyBaseError as VyBaseError3 } from "@vy/err";
var ItemNotFoundError = class extends VyBaseError3 {
  constructor(array, message, errorCode) {
    super({
      code: errorCode ?? ARRAY_ERROR_CODES.ITEM_NOT_FOUND,
      message,
      metadata: {
        array
      }
    });
  }
};

// src/services/array-accessor.ts
function getRequiredFirst(array, arrayName) {
  return getRequiredAt(array, 0, arrayName);
}
function getRequiredAt(array, index, arrayName) {
  const item = array.at(index);
  raiseIfNullish(item, new ArrayOutOfBoundsError(arrayName, array, index));
  return item;
}
function getFirstOrNull(array) {
  return array.at(0) ?? null;
}
function getLastOrNull(array) {
  return array.at(-1) ?? null;
}
function findRequired(array, predicate) {
  const item = array.find(predicate);
  raiseIfNullish(item, new ItemNotFoundError(array, "Item not found"));
  return item;
}
function extractIds(array) {
  return array.map(({ id }) => id);
}

// src/services/array-checks.ts
import { raise } from "@vy/err";
import { notDefined } from "@vy/null";
function raiseIfNotSingleItem(array, arrayName) {
  raiseIfUnexpectedSize(array, 1, arrayName);
}
function raiseIfUnexpectedSize(array, expectedSize, arrayName) {
  if (array.length !== expectedSize) {
    raise(new UnexpectedArraySizeError(arrayName, array, expectedSize));
  }
}
function raiseIfEmpty(array, emptyArrayError) {
  if (notDefined(array) || array.length === 0) {
    raise(emptyArrayError);
  }
}
function validateNotEmptyArray(array, arrayName) {
  if (isEmpty(array)) {
    throw new Error(`${arrayName ?? "array"} is empty`);
  }
}

// src/services/array-operations.ts
import { isFunction } from "@vy/function";
import { defined as defined2 } from "@vy/null";
import { getOptionalObjectProperty } from "@vy/object";
function purge(array) {
  return array.filter(defined2);
}
function purgeExtract(array, key) {
  const extractedItems = extract(array, key);
  return purge(extractedItems);
}
function extract(items, keyOrAccessor) {
  if (isEmpty(items)) {
    return [];
  }
  const accessor = isFunction(keyOrAccessor) ? keyOrAccessor : (item) => item[keyOrAccessor];
  return items.map((item) => accessor(item));
}
function flatExtract(items, keyOrAccessor) {
  const extractedItems = extract(items, keyOrAccessor);
  return extractedItems.flat();
}
function extractByKey(items, key) {
  if (isEmpty(items)) {
    return [];
  }
  return items.map((item) => item[key]);
}
function sortBy(array, accessor) {
  return [...array].sort((a, b) => accessor(a) - accessor(b));
}
function sortByDesc(array, accessor) {
  return sortBy(array, (item) => accessor(item)).reverse();
}
function intersect(array1, array2) {
  return array1.filter((item) => array2.includes(item));
}
function uniqueBy(array, accessor) {
  return array.filter((itemA, index) => {
    const itemIndex = array.findIndex((itemB) => accessor(itemB) === accessor(itemA));
    return itemIndex === index;
  });
}
function unique(array) {
  return [...new Set(array)];
}
function uniqueExtract(items, keyOrAccessor) {
  if (!isFunction(keyOrAccessor)) {
    return uniqueExtract(items, (item) => getOptionalObjectProperty(item, keyOrAccessor));
  }
  const extractedItems = extract(items, keyOrAccessor);
  return unique(extractedItems);
}

// src/services/batch.ts
import { raiseIfNullish as raiseIfNullish2 } from "@vy/null";
var EMPTY_BATCH = {
  currentBatchSize: 0,
  items: []
};
function getLast(array) {
  const last = array.at(-1);
  raiseIfNullish2(last, new ArrayOutOfBoundsError("array", array, -1));
  return last;
}
function batch(items, batchSize) {
  const numberOfBatches = Math.ceil(items.length / batchSize);
  return Array.from({ length: numberOfBatches }).map(
    (_, batchIndex) => items.slice(batchIndex * batchSize, (batchIndex + 1) * batchSize)
  );
}
function batchByMaxPayloadSize(items, maxPayloadSize, toPayload) {
  const sizeLimitedBatches = items.reduce(
    (batchIterationData, item) => {
      const payload = toPayload(item);
      const payloadSize = Buffer.byteLength(payload, "utf8");
      const currentBatch = getLast(batchIterationData);
      if (currentBatch.currentBatchSize + payloadSize > maxPayloadSize) {
        const newBatch = {
          currentBatchSize: payloadSize,
          items: [item]
        };
        return [...batchIterationData, newBatch];
      }
      const completedBatches = batchIterationData.slice(0, -1);
      const updatedCurrentBatch = {
        currentBatchSize: currentBatch.currentBatchSize + payloadSize,
        items: [...currentBatch.items, item]
      };
      return [...completedBatches, updatedCurrentBatch];
    },
    [EMPTY_BATCH]
  );
  return extract(sizeLimitedBatches, "items");
}

// src/services/index.ts
import { groupBy, range } from "lodash";
export {
  ARRAY_ERROR_CODES,
  ArrayIsEmptyError,
  ArrayOutOfBoundsError,
  ItemNotFoundError,
  UnexpectedArraySizeError,
  batch,
  batchByMaxPayloadSize,
  containsManyItems,
  extract,
  extractByKey,
  extractIds,
  findRequired,
  flatExtract,
  getFirstOrNull,
  getLastOrNull,
  getRequiredAt,
  getRequiredFirst,
  groupBy,
  intersect,
  isArray,
  isEmpty,
  isNotEmpty,
  isNotIn,
  isSingleItem,
  purge,
  purgeExtract,
  raiseIfEmpty,
  raiseIfNotSingleItem,
  raiseIfUnexpectedSize,
  range,
  sortBy,
  sortByDesc,
  unique,
  uniqueBy,
  uniqueExtract,
  validateNotEmptyArray
};
